import type { ComponentType } from "react";
import ColorsComponent from "./colors";
import IconsComponent from "./icons";
import OverviewComponent from "./overview.mdx";
import TypographyComponent from "./typography";

export interface ComponentRegistryEntry {
  component: ComponentType;
  name: string;
  section: "Start Here" | "Design Tokens" | "Components";
  subheader?: string;
  externalLinks?: { label: string; url: string }[];
  /** Parent directory for dynamic components (e.g., "buttons", "forms") */
  group?: string;
}

// Helper functions for dynamic loading
export function createSlug(componentName: string): string {
  return componentName
    .replace(/([A-Z])/g, "-$1")
    .toLowerCase()
    .replace(/^-/, "");
}

export function extractComponentName(path: string): string {
  const pathParts = path
    .replace(/^\.\//, "")
    .replace(/\.example\.(tsx|mdx)$/, "")
    .split("/");
  return pathParts[pathParts.length - 1];
}

/** Extract the top-level directory from a path (e.g., "./buttons/Button/Button.example.tsx" -> "buttons") */
export function extractParentDirectory(path: string): string {
  const pathParts = path
    .replace(/^\.\.?\//, "")
    .replace(/\.example\.(tsx|mdx)$/, "")
    .split("/");
  return pathParts[0] || "other";
}

// Function to dynamically load component examples at runtime (browser only)
export function loadDynamicComponents(): Record<string, ComponentRegistryEntry> {
  const dynamicEntries: Record<string, ComponentRegistryEntry> = {};

  // Only run in browser environment, not during SSR/build
  if (typeof window === "undefined") {
    return dynamicEntries;
  }

  try {
    // Vite compile-time transform: discovers all example files and eagerly imports them.
    // import.meta.glob is replaced at build time — it is NOT a runtime function.
    const modules = import.meta.glob<{ default?: ComponentType }>("../**/*.example.{tsx,mdx}", { eager: true });

    for (const [examplePath, module] of Object.entries(modules)) {
      const componentName = extractComponentName(examplePath);
      const slug = createSlug(componentName);
      const group = extractParentDirectory(examplePath);
      const ExampleComponent = module.default;

      if (ExampleComponent) {
        dynamicEntries[slug] = {
          component: ExampleComponent,
          name: componentName,
          section: "Components",
          group,
        };
      }
    }
  } catch (error) {
    console.warn("Failed to load dynamic component examples:", error);
  }

  return dynamicEntries;
}

// No more build-time registry needed! Dynamic components are loaded at runtime.

// Static registry for foundation components and special pages
export const staticRegistry: Record<string, ComponentRegistryEntry> = {
  "": {
    component: OverviewComponent,
    name: "Documentation Overview",
    section: "Start Here",
    subheader:
      "Higharc's component library is a place where design and engineering can find, understand and document components used in Config and Procure.",
    externalLinks: [],
  },
  colors: {
    component: ColorsComponent,
    name: "Colors",
    section: "Design Tokens",
    subheader: "Colors are used to style the UI.",
    externalLinks: [
      {
        label: "Figma",
        url: "https://www.figma.com/design/uqCmATAVpkFx2nt7Aii37o/%F0%9F%91%BE-Arcade-%E2%80%93-Global-Base-Styles?node-id=6213-6262&p=f&t=pkK5ep0nakV9nFX4-0",
      },
    ],
  },
  icons: {
    component: IconsComponent,
    name: "Icons",
    section: "Design Tokens",
    subheader: "Icons are used to provide visual cues and improve user experience.",
    externalLinks: [
      {
        label: "Figma",
        url: "https://www.figma.com/design/DvJihuneOAPAPnZR4wZQhT/%F0%9F%93%90-Arcade-UI?node-id=6888-89140&t=Zfv0FMUqgAOeqGjG-0",
      },
    ],
  },
  typography: {
    component: TypographyComponent,
    name: "Typography",
    section: "Design Tokens",
    subheader: "Common typographic styles found in `ui/styles/fontStyles.scss`",
    externalLinks: [
      {
        label: "Figma",
        url: "https://www.figma.com/design/qAdectgD6kYZM2xCJGo3o2/10-2024-%F0%9F%91%BE-Typography-v0.1?node-id=44-3440&t=BVZoD0Jy9xFvkeMe-0",
      },
    ],
  },
};
